import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from 'lucide-react';
export function HeroSection() {
  const scrollToPricing = () => {
    const target = document.getElementById('pricing');
    if (!target) return;
    const start = window.scrollY;
    const end = target.getBoundingClientRect().top + start - 80;
    const duration = 2000;
    const startTime = performance.now();
    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start + (end - start) * easeInOutCubic(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  return <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-12 pb-16" data-id="element-158">
      {/* Dynamic Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-159">
        {/* Animated Glows */}
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1000px] h-[800px] bg-[#00f0ff]/10 rounded-full blur-[140px] will-change-transform" data-id="element-160" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-[-10%] right-[-5%] w-[800px] h-[800px] bg-[#8b5cf6]/10 rounded-full blur-[140px] will-change-transform" data-id="element-161" />

        {/* Grid Pattern with Mask */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)] opacity-40" data-id="element-162" />

        {/* Floating Particles */}
        {[...Array(15)].map((_, i) => <motion.div key={i} initial={{
        x: Math.random() * 100 + '%',
        y: Math.random() * 100 + '%',
        opacity: Math.random() * 0.5
      }} animate={{
        y: [null, '-20px', '20px', null],
        opacity: [null, 0.8, 0.4, null]
      }} transition={{
        duration: 3 + Math.random() * 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute w-1 h-1 bg-[#00f0ff] rounded-full blur-[1px]" data-id="element-163" />)}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center flex flex-col items-center" data-id="element-164">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1]
      }} className="w-full" data-id="element-165">
          {/* Animated Badge */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#130a2a] border border-[#00f0ff]/30 text-[#00f0ff] text-sm font-bold mb-8 shadow-[0_0_15px_rgba(0,240,255,0.2)]" data-id="element-166">
            <span className="relative flex h-2 w-2" data-id="element-167">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-75" data-id="element-168"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00f0ff]" data-id="element-169"></span>
            </span>
            CONVERTITE EN UN EXPERTO Y CREÁ TU PROPIO NEGOCIO
          </motion.div>

          <h1 className="text-5xl md:text-8xl lg:text-[6.5rem] font-black text-white leading-[0.95] tracking-tighter mb-8 uppercase" data-id="element-170">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f0ff] to-[#8b5cf6]" data-id="element-171">
              MESAS Y ESPEJOS INFINITOS
            </span>
          </h1>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 mt-4" data-id="element-172">
            {/* Right Side: Book Presentation (Moved to start) */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8,
            rotate: -5
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative group flex-1 max-w-md" data-id="element-173">
              {/* Glow behind book */}
              <div className="absolute inset-0 bg-[#00f0ff]/20 blur-[80px] rounded-full scale-75 group-hover:scale-90 transition-transform duration-700" data-id="element-174" />

              {/* Main Image with floating animation */}
              <motion.div animate={{
              y: [0, -20, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative z-10" data-id="element-175">
                <img src="https://cdn.magicpatterns.com/uploads/kHbmhMsckcZTtu8vtZRc3v/ebook.png" alt="Ebook Mesas y Espejos Infinitos" width={800} height={800} fetchPriority="high" loading="eager" decoding="sync" className="w-full h-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)]" data-id="element-176" />

                {/* Reflection Effect */}
                <div className="absolute top-[95%] left-0 w-full h-40 overflow-hidden pointer-events-none opacity-20 [mask-image:linear-gradient(to_bottom,black,transparent)]" data-id="element-177">
                  <img src="https://cdn.magicpatterns.com/uploads/kHbmhMsckcZTtu8vtZRc3v/ebook.png" alt="" className="w-full h-auto scale-y-[-1] origin-top blur-[4px]" aria-hidden="true" data-id="element-178" />
                </div>
              </motion.div>
            </motion.div>

            {/* Left Side: Text and CTA (Moved after image) */}
            <div className="flex-1 text-center lg:text-left max-w-xl" data-id="element-179">
              <p className="text-xl md:text-2xl text-gray-300 mb-10 font-medium leading-relaxed" data-id="element-180">
                Dejá de improvisar y empezá a crear piezas de lujo que todos
                quieran comprar. La guía paso a paso para resultados
                profesionales que{' '}
                <span className="text-white font-bold underline decoration-[#00f0ff]/50 decoration-2 underline-offset-4" data-id="element-181">
                  transformará tu hobby en un negocio.
                </span>
              </p>

              <div className="flex flex-col items-center lg:items-start gap-8" data-id="element-182">
                <motion.button whileHover={{
                scale: 1.05,
                boxShadow: '0 0 30px rgba(245, 197, 24, 0.4)'
              }} whileTap={{
                scale: 0.95
              }} onClick={scrollToPricing} className="relative group overflow-hidden bg-gradient-to-b from-[#f5c518] to-[#d97706] text-black font-black text-xl md:text-2xl px-12 py-6 rounded-2xl uppercase tracking-wider transition-all border-b-4 border-[#92400e] w-full md:w-auto" data-id="element-183">
                  <span className="relative z-10" data-id="element-184">QUIERO ACCEDER AHORA</span>
                  {/* Shimmer effect */}
                  <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" data-id="element-185" />
                </motion.button>

                {/* Enhanced Social Proof */}
                <div className="flex flex-col items-center lg:items-start gap-3" data-id="element-186">
                  <div className="flex items-center gap-4" data-id="element-187">
                    <div className="flex -space-x-3" data-id="element-188">
                      {[1, 2, 3, 4].map(i => <img key={i} src={`https://randomuser.me/api/portraits/men/${i + 20}.jpg`} alt="User" className="w-10 h-10 rounded-full border-2 border-[#0a0514] shadow-lg" data-id="element-189" />)}
                      <div className="w-10 h-10 rounded-full bg-[#130a2a] border-2 border-[#0a0514] flex items-center justify-center text-[10px] font-bold text-[#00f0ff]" data-id="element-190">
                        +1k
                      </div>
                    </div>
                    <div className="flex flex-col items-start" data-id="element-191">
                      <div className="flex gap-0.5" data-id="element-192">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-[#f5c518] text-[#f5c518]" data-id="element-193" />)}
                      </div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-id="element-194">
                        Calificación 4.9/5
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}